import React, { useState } from 'react';
import {
  useTranslation,
  useEnvironment,
  useExperiments,
} from '@wix/yoshi-flow-editor';
import { Calendar } from '@wix/wix-ui-icons-common/on-stage';
import { ActionsMenuLayout } from 'wix-ui-tpa/cssVars';
import Button from '../../../Components/Button';
import { classes, st } from './BookAgain.st.css';
import { classes as classesActionsMenu } from '../../ManageButton/ManageButton.st.css';
import {
  ActionButtonProps,
  ActionButtonUiTypes,
  ActionsMenuType,
  BookingsGroup,
} from '../../../../../types';
import { ActionsButtonsDataHooks, BookAgainDataHooks } from '../../datahooks';
import { handleWithStopPropagation } from '../../../../../utils';
import { useApplicationContext } from '../../../../../core/hooks/useApplicationContext';
import { ExperimentsConsts } from '../../../../../consts/experiments';

export interface BookAgainProps extends ActionButtonProps {
  group: BookingsGroup;
}

export const BookAgain: React.FC<BookAgainProps> = ({
  group,
  onActionClick,
  type,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const { experiments } = useExperiments();
  const isBookAgainControllerEnabled = experiments.enabled(
    ExperimentsConsts.BookAgainController,
  );
  const { actions } = useApplicationContext();
  const [loading, setLoading] = useState(false);
  let content;

  if (type === ActionButtonUiTypes.MENU) {
    const onBookAgainMenuClick = () => {
      onActionClick?.(ActionsMenuType.BOOK_AGAIN, {
        showManageButtonLoader: true,
      });
      actions.clickOnBookAgain({ group });
    };

    content = (
      <div
        className={st(classes.actionMenuBookAgainButton, {
          isBookAgainControllerEnabled,
        })}
      >
        <ActionsMenuLayout
          className={classesActionsMenu.actionMenuLayout}
          data-hook={ActionsButtonsDataHooks.BookAgainMenu}
        >
          <ActionsMenuLayout.Item
            onClick={onBookAgainMenuClick}
            prefixIcon={<Calendar />}
            content={t('app.my-bookings-widget.book-another.button.label')}
          />
        </ActionsMenuLayout>
      </div>
    );
  }

  if (type === ActionButtonUiTypes.BUTTON) {
    const onBookAgainButtonClick = () => {
      onActionClick?.(ActionsMenuType.BOOK_AGAIN);
      setLoading(true);
      actions.clickOnBookAgain({ group });
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        onBookAgainButtonClick();
      }
    };

    content = (
      <div
        className={st(classes.bookAgainButtonForPastBookings, {
          isBookAgainControllerEnabled,
        })}
      >
        <Button
          loading={loading}
          className={classes.bookAgainButton}
          data-hook={ActionsButtonsDataHooks.BookAgainButton}
          onClick={(e) => handleWithStopPropagation(e, onBookAgainButtonClick)}
          onKeyDown={(e) => handleWithStopPropagation(e, handleKeyDown)}
        >
          {t('app.my-bookings-widget.book-another.button.label')}
        </Button>
      </div>
    );
  }

  return (
    <div
      className={st(classes.root, {
        isMobile,
        buttonType: type,
      })}
      data-hook={BookAgainDataHooks.Main}
    >
      {content}
    </div>
  );
};
